import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { SpaqFireDb } from './spaq-fire-db.service';

/** Where visitors from a scope under maintenance get sent instead of the normal app. */
const MAINTENANCE_URL = 'https://spaq-abonnement-maintenance.web.app/';

/**
 * Redirects the whole page to the maintenance site when the current org scope — read straight off
 * the URL's first hostname label (e.g. "cem" from cem.spaq.com) — is listed under RTDB's
 * /config/redirects (an array of scope codes, e.g. ["CEM", "SAAQ"]). Never redirects when the
 * visitor is on the public domain (hostname starting with "public"), even if the derived scope
 * happens to match an entry.
 *
 * Call `check()` once at app bootstrap (see AppComponent.ngOnInit). Unlike a one-shot read, this
 * keeps listening to /config/redirects for the life of the app (no take(1)/unsubscribe) — so if
 * staff add the current scope to the list while a tab is already open, that tab reacts and
 * redirects immediately, rather than only on the next reload.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceScopeRedirectService {
	constructor(
		private db: SpaqFireDb,
		private afAuth: AngularFireAuth
	) {}

	check(): void {
		const [scope] = window.location.hostname.split('.');
		if (!scope) {
			return;
		}

		const normalizedScope = scope.toLowerCase();
		this.db.maintenanceScopes$().subscribe({
			next: scopes => {
				const isRedirected =
					Array.isArray(scopes) && scopes.some(entry => typeof entry === 'string' && entry.toLowerCase() === normalizedScope);
				if (isRedirected) {
					this.logoutAndRedirect();
				}
			},
			// Fail-open — an RTDB hiccup here shouldn't block the whole app from loading.
			error: () => undefined
		});
	}

	/** Signs the visitor out before leaving for the maintenance site (same goOffline + signOut
	 *  sequence as KillSwitchService.forceLogoutAndRefresh) — a maintenance window isn't a place
	 *  to leave an authenticated session sitting in a background tab. A signOut failure never
	 *  blocks the redirect itself. */
	private logoutAndRedirect(): void {
		this.db.goOffline();
		this.afAuth
			.signOut()
			.catch(() => undefined)
			.finally(() => {
				window.location.href = MAINTENANCE_URL;
			});
	}
}
